<script setup lang="ts">
    import { computed } from 'vue'

    import { copyToClipboard } from '@common/Common'

    import type { ScoringPanelistReadModel } from '@Evaluation/AlternativesSurveys/Domain/readmodels'
    import { ScoringSurveyAccessLinksViewModel } from '@Evaluation/AlternativesSurveys/Components/ScoringSurveyAccessLinks'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        viewModel: ScoringSurveyAccessLinksViewModel
    }>()

    const emit = defineEmits<{
        (e: 'download-links', surveyId: number): void
        (e: 'show-email-sender', surveyId: number, projectUserIds: number[]): void
    }>()

    /* ---------------- shortcuts ---------------- */

    const vm = computed(() => props.viewModel)
    const opened = computed(() => vm.value?.opened ?? false)

    const initialized = computed(() => vm.value.data != null)
    const surveyId = computed(() => vm.value.surveyId)
    const panelists = computed<ScoringPanelistReadModel[]>(() => vm.value.data?.Panelists ?? [])

    /* ---------------- actions ---------------- */

    function copyAccessUrlToClipboard(p: ScoringPanelistReadModel)
    {
        copyToClipboard(p.AccessUrl)

        const url = p.AccessUrl
        p.AccessUrl = 'Copied to clipboard'

        const handle = setTimeout(() =>
        {
            p.AccessUrl = url
            clearTimeout(handle)
        }, 500)
    }

    function downloadLinks()
    {
        emit('download-links', surveyId.value)
    }

    function emailOne(p: ScoringPanelistReadModel)
    {
        emit('show-email-sender', surveyId.value, [p.ProjectUserId])
    }

    function emailAll()
    {
        emit(
            'show-email-sender',
            surveyId.value,
            panelists.value.map(p => p.ProjectUserId)
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Access links</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h3>

                        <div v-if="initialized">
                            <table class="table table-hover">
                                <thead class="thead-light">
                                    <tr>
                                        <th>
                                            Panelist
                                        </th>
                                        <th>
                                            Access link
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="p in panelists" :key="p.ProjectUserId">
                                        <td>
                                            <span>{{p.Name}}</span><br />
                                            <span class="text-muted" v-if="p.HasEmail">{{p.Email}}</span>
                                            <span class="text-danger" v-if="!p.HasEmail">Email not provided</span>
                                        </td>
                                        <td class="align-middle">
                                            <div class="input-group">

                                                <input type="text" class="form-control" readonly v-model="p.AccessUrl" />
                                                <div class="input-group-append">
                                                    <button class="btn btn-outline-secondary" type="button" title="Copy to clipboard" @click="copyAccessUrlToClipboard(p)">
                                                        <i class="fa fa-clipboard" aria-hidden="true"></i>
                                                    </button>
                                                    <button class="btn btn-outline-secondary" type="button" title="Send link via email" :disabled="!p.HasEmail" @click="emailOne(p)">
                                                        <i class="fa-regular fa-envelope"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>


                    <div class="modal-footer">
                        <button @click="downloadLinks" :disabled="!initialized" type="button" class="btn btn-outline-secondary">Download links</button>
                        <button @click="emailAll" :disabled="!initialized" type="button" class="btn btn-outline-secondary">Email all</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>